import { useEffect, useState } from "react";
import { ALLIANCE_BANNERS, ALLIANCE_CREATE_CREDITS, ALLIANCE_CREATE_HQ, ALLIANCE_ROLES, allianceCan, allianceRank, type AllianceRole } from "@zend/content";
import { Icon } from "../ui/Icon.js";
import { Button } from "../ui/Button.js";
import { Panel } from "../ui/Panel.js";
import { formatCompact } from "../ui/Counter.js";
import { useT } from "../i18n/index.js";
import { navigate } from "../router.js";
import { useSession } from "../state/session.js";
import { useSocial, lastSeenText } from "../social/state.js";
import { relativeTime } from "../social/mailModel.js";
import { getLocale } from "../i18n/index.js";
import { serverNow } from "../state/serverTime.js";
import { allianceApi } from "./api.js";
import { ALLIANCE_TABS, useAlliance, type AllianceTab } from "./state.js";
import type { AllianceListEntry, AllianceMember, AllianceView } from "./types.js";
import { Contribute } from "./Contribute.js";
import { Buildings } from "./Buildings.js";
import { Gifts } from "./Gifts.js";
import { AllianceShop } from "./AllianceShop.js";
import { Diplomacy } from "./Diplomacy.js";
import { AllianceEvents } from "./AllianceEvents.js";
import "./alliance.css";

/** `/app/alliance[/tab]`: Find or Create without an alliance; Overview · Members · Requests · Log with one (82-alliance.md R77, D71). */
export function AllianceHub({ route }: { route: string }) {
  const t = useT();
  const alliance = useAlliance((s) => s.alliance);
  const hubGifts = useAlliance((s) => s.gifts);
  const hubDiplomacy = useAlliance((s) => s.diplomacy);
  const refresh = useAlliance((s) => s.refresh);
  const seg = route.split("/")[3] as AllianceTab | undefined;
  const tab: AllianceTab = seg && ALLIANCE_TABS.includes(seg) ? seg : "overview";
  useEffect(() => {
    void refresh();
  }, [refresh]);
  if (alliance === undefined) return <div className="page alliance" data-testid="alliance-hub"><p className="hint" style={{ padding: 12 }}>{t("common.loading")}</p></div>;
  if (alliance === null) return <div className="page alliance" data-testid="alliance-hub"><FindAlliance /></div>;
  const officer = alliance.myRole !== null && allianceCan(alliance.myRole, "accept");
  const tabs = ALLIANCE_TABS.filter((x) => x !== "requests" || officer || true);
  return (
    <div className="page alliance" data-testid="alliance-hub">
      <AllianceBanner alliance={alliance} />
      <div className="row tabs" style={{ padding: "0 12px 8px" }}>
        {tabs.map((x) => (
          <button key={x} type="button" className={`tab ${x === tab ? "active" : ""}`} onClick={() => navigate(x === "overview" ? "/app/alliance" : `/app/alliance/${x}`)} data-testid={`alliance-tab-${x}`}>
            {t(`alliance.tab.${x}`)}
            {x === "requests" && alliance.openRequests > 0 ? <span className="badge warn" style={{ marginLeft: 6 }}>{alliance.openRequests}</span> : null}
            {x === "gifts" && hubGifts && hubGifts.claimable > 0 ? <span className="badge gold" style={{ marginLeft: 6 }}>{hubGifts.claimable}</span> : null}
            {x === "diplomacy" && hubDiplomacy?.canAct && hubDiplomacy.proposals.incoming.length > 0 ? <span className="badge warn" style={{ marginLeft: 6 }}>{hubDiplomacy.proposals.incoming.length}</span> : null}
          </button>
        ))}
      </div>
      {tab === "overview" ? <Overview alliance={alliance} /> : null}
      {tab === "members" ? <Members alliance={alliance} /> : null}
      {tab === "contribute" ? <Contribute alliance={alliance} /> : null}
      {tab === "buildings" ? <Buildings alliance={alliance} /> : null}
      {tab === "shop" ? <AllianceShop /> : null}
      {tab === "gifts" ? <Gifts /> : null}
      {tab === "diplomacy" ? <Diplomacy alliance={alliance} /> : null}
      {tab === "events" ? <AllianceEvents /> : null}
      {tab === "requests" ? <Requests alliance={alliance} /> : null}
      {tab === "log" ? <Log /> : null}
    </div>
  );
}

function AllianceBanner({ alliance }: { alliance: AllianceView }) {
  const t = useT();
  return (
    <header className={`alliance-banner banner-${alliance.banner}`} data-testid="alliance-banner">
      <div className="alliance-tag">{alliance.tag}</div>
      <div className="grow" style={{ minWidth: 0 }}>
        <h2 className="ellipsis" data-testid="alliance-name">{alliance.name}</h2>
        <div className="hint">{t("alliance.level", { n: alliance.level })} · {t("alliance.power")} {formatCompact(alliance.power)} · {t("alliance.members", { n: alliance.memberCount, max: alliance.cap })}</div>
      </div>
      {alliance.myRole ? <span className="badge gold" data-testid="alliance-my-role">{t(`alliance.role.${alliance.myRole}`)}</span> : null}
    </header>
  );
}

/** Without an alliance: search and join or apply, or found one (HQ 3, 100 ₵). */
export function FindAlliance() {
  const t = useT();
  const list = useAlliance((s) => s.list);
  const search = useAlliance((s) => s.search);
  const join = useAlliance((s) => s.join);
  const requests = useAlliance((s) => s.requests);
  const refreshRequests = useAlliance((s) => s.refreshRequests);
  const run = useAlliance((s) => s.run);
  const refresh = useAlliance((s) => s.refresh);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  useEffect(() => {
    void search("");
    void refreshRequests();
  }, [search, refreshRequests]);
  const acceptInvite = async (id: string) => {
    if (await run(() => allianceApi.acceptInvite(id), t("alliance.toast.joined"))) await refresh();
  };
  return (
    <div className="alliance-find" data-testid="alliance-find">
      <Panel title={t("alliance.find.title")}>
        <p className="hint">{t("alliance.find.hint")}</p>
        {requests?.invites.length ? (
          <div className="alliance-invites" data-testid="alliance-invites">
            {requests.invites.map((r) => (
              <div key={r.id} className="row alliance-row" data-testid={`alliance-invite-${r.allianceId}`}>
                <span className="grow"><strong>{r.allianceName}</strong> [{r.allianceTag}] · {t("alliance.invitedBy", { name: r.createdByName })}</span>
                <Button variant="primary" style={{ minHeight: 36 }} onClick={() => void acceptInvite(r.id)} data-testid="alliance-invite-accept">{t("alliance.accept")}</Button>
                <Button style={{ minHeight: 36 }} onClick={() => void run(() => allianceApi.declineInvite(r.id)).then(() => refreshRequests())}>{t("alliance.decline")}</Button>
              </div>
            ))}
          </div>
        ) : null}
        <div className="row" style={{ gap: 8 }}>
          <input className="field grow" value={query} placeholder={t("alliance.find.placeholder")} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void search(query); }} data-testid="alliance-search" />
          <Button onClick={() => void search(query)} data-testid="alliance-search-go"><Icon name="search" /></Button>
          <Button variant="gold" onClick={() => setCreating((v) => !v)} data-testid="alliance-create-toggle"><Icon name="plus" /> {t("alliance.create.title")}</Button>
        </div>
        {creating ? <CreateAlliance onDone={() => setCreating(false)} /> : null}
        {!list ? <p className="hint">{t("common.loading")}</p> : list.length === 0 ? <p className="hint" data-testid="alliance-none">{t("alliance.find.none")}</p> : null}
        {list?.map((a) => <AllianceRow key={a.id} entry={a} onJoin={() => void join(a.id)} />)}
      </Panel>
    </div>
  );
}

function AllianceRow({ entry, onJoin }: { entry: AllianceListEntry; onJoin: () => void }) {
  const t = useT();
  const hq = useSession((s) => s.account ? undefined : undefined);
  void hq;
  const full = entry.memberCount >= entry.cap;
  const label = entry.joinPolicy === "closed" ? t("alliance.closed") : entry.applied ? t("alliance.applied") : entry.joinPolicy === "approval" ? t("alliance.apply") : t("alliance.join");
  return (
    <div className={`alliance-row banner-${entry.banner}`} data-testid={`alliance-entry-${entry.id}`}>
      <span className="alliance-tag small">{entry.tag}</span>
      <span className="grow" style={{ minWidth: 0 }}>
        <strong className="ellipsis">{entry.name}</strong>
        <span className="hint">{t("alliance.level", { n: entry.level })} · {formatCompact(entry.power)} · {entry.memberCount}/{entry.cap} · {t("alliance.leader")} {entry.leaderName}{entry.minHq > 1 ? ` · HQ ${entry.minHq}+` : ""}</span>
      </span>
      <Button variant={entry.joinPolicy === "open" && !full ? "primary" : "default"} disabled={full || entry.joinPolicy === "closed" || entry.applied} style={{ minHeight: 36 }} onClick={onJoin} data-testid={`alliance-join-${entry.id}`}>
        {full ? t("alliance.full") : label}
      </Button>
    </div>
  );
}

function CreateAlliance({ onDone }: { onDone: () => void }) {
  const t = useT();
  const create = useAlliance((s) => s.create);
  const [name, setName] = useState("");
  const [tag, setTag] = useState("");
  const [description, setDescription] = useState("");
  const [banner, setBanner] = useState<(typeof ALLIANCE_BANNERS)[number]>("steel");
  const [joinPolicy, setJoinPolicy] = useState<"open" | "approval" | "closed">("open");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      if (await create({ name: name.trim(), tag: tag.trim().toUpperCase(), description: description.trim(), banner, joinPolicy, minHq: 1 })) onDone();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="alliance-create" data-testid="alliance-create">
      <p className="hint">{t("alliance.create.hint", { hq: ALLIANCE_CREATE_HQ, credits: ALLIANCE_CREATE_CREDITS })}</p>
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <input className="field grow" value={name} maxLength={24} placeholder={t("alliance.create.name")} onChange={(e) => setName(e.target.value)} data-testid="alliance-create-name" />
        <input className="field" style={{ width: 90 }} value={tag} maxLength={4} placeholder={t("alliance.create.tag")} onChange={(e) => setTag(e.target.value.toUpperCase())} data-testid="alliance-create-tag" />
      </div>
      <input className="field" style={{ width: "100%", marginTop: 8 }} value={description} maxLength={200} placeholder={t("alliance.create.description")} onChange={(e) => setDescription(e.target.value)} data-testid="alliance-create-description" />
      <div className="row" style={{ gap: 6, marginTop: 8, flexWrap: "wrap" }}>
        {ALLIANCE_BANNERS.map((b) => (
          <button key={b} type="button" className={`alliance-swatch banner-${b} ${banner === b ? "active" : ""}`} aria-pressed={banner === b} onClick={() => setBanner(b)} title={b} data-testid={`alliance-banner-${b}`} />
        ))}
        <span className="grow" />
        <select className="field" value={joinPolicy} onChange={(e) => setJoinPolicy(e.target.value as typeof joinPolicy)} data-testid="alliance-create-policy">
          {(["open", "approval", "closed"] as const).map((p) => <option key={p} value={p}>{t(`alliance.policy.${p}`)}</option>)}
        </select>
      </div>
      <div className="row" style={{ gap: 8, marginTop: 10 }}>
        <span className="grow" />
        <Button onClick={onDone}>{t("common.cancel")}</Button>
        <Button variant="gold" disabled={busy || name.trim().length < 3 || tag.trim().length < 2} loading={busy} onClick={() => void submit()} data-testid="alliance-create-submit">{t("alliance.create.submit")}</Button>
      </div>
    </div>
  );
}

function Overview({ alliance }: { alliance: AllianceView }) {
  const t = useT();
  const help = useAlliance((s) => s.help);
  const gifts = useAlliance((s) => s.gifts);
  const helpAll = useAlliance((s) => s.helpAll);
  const leave = useAlliance((s) => s.leave);
  const run = useAlliance((s) => s.run);
  const refresh = useAlliance((s) => s.refresh);
  const [notice, setNotice] = useState(alliance.notice);
  const [confirmLeave, setConfirmLeave] = useState(false);
  useEffect(() => setNotice(alliance.notice), [alliance.notice]);
  const canNotice = alliance.myRole !== null && allianceCan(alliance.myRole, "notice");
  const helpable = help?.filter((h) => h.canHelp).length ?? 0;
  const saveNotice = async () => {
    if (await run(() => allianceApi.update({ notice }), t("alliance.toast.noticeSaved"))) await refresh();
  };
  return (
    <div className="alliance-overview" data-testid="alliance-overview">
      <Panel title={t("alliance.notice")}>
        {canNotice ? (
          <>
            <textarea className="field" style={{ width: "100%", minHeight: 72 }} value={notice} maxLength={500} onChange={(e) => setNotice(e.target.value)} data-testid="alliance-notice-input" />
            <div className="row" style={{ marginTop: 6 }}><span className="grow" /><Button disabled={notice === alliance.notice} onClick={() => void saveNotice()} data-testid="alliance-notice-save">{t("common.save")}</Button></div>
          </>
        ) : (
          <p data-testid="alliance-notice">{alliance.notice || t("alliance.noNotice")}</p>
        )}
        {alliance.description ? <p className="hint">{alliance.description}</p> : null}
      </Panel>
      <Panel title={t("alliance.help.title")}>
        <p className="hint">{t("alliance.help.hint")}</p>
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <Button variant={helpable > 0 ? "primary" : "default"} disabled={helpable === 0} onClick={() => void helpAll()} data-testid="alliance-help-all">
            <Icon name="people" /> {t("alliance.help.all")} {helpable > 0 ? `(${helpable})` : ""}
          </Button>
          <Button onClick={() => navigate("/app/chat")} data-testid="alliance-open-chat"><Icon name="chat" /> {t("alliance.chat")}</Button>
          <Button onClick={() => navigate("/app/alliance/contribute")} data-testid="alliance-open-contribute"><Icon name="gather" /> {t("alliance.contribute")}</Button>
          <Button onClick={() => navigate("/app/alliance/shop")} data-testid="alliance-open-shop"><Icon name="shop" /> {t("alliance.shop")}</Button>
          <Button onClick={() => navigate("/app/alliance/events")} data-testid="alliance-open-events"><Icon name="attack" /> {t("alliance.tab.events")}</Button>
          <Button onClick={() => navigate("/app/alliance/diplomacy")} data-testid="alliance-open-diplomacy"><Icon name="flag" /> {t("alliance.tab.diplomacy")}</Button>
          <Button onClick={() => navigate("/app/alliance/gifts")} data-testid="alliance-open-gifts"><Icon name="bookmark" /> {t("alliance.tab.gifts")}{gifts && gifts.claimable > 0 ? <span className="badge gold" style={{ marginLeft: 6 }}>{gifts.claimable}</span> : null}</Button>
        </div>
        {help?.length ? (
          <ul className="alliance-help-list" data-testid="alliance-help-list">
            {help.map((h) => (
              <li key={h.rowId} className="row" data-testid={`alliance-help-${h.accountId}`} data-can-help={h.canHelp}>
                <span className="grow">{h.name} · {t("alliance.help.building", { n: h.level })} · {h.helpers} {t("alliance.help.helpers")}</span>
                <span className={`badge ${h.canHelp ? "ok" : ""}`}>{h.canHelp ? t("alliance.help.open") : t("alliance.help.done")}</span>
              </li>
            ))}
          </ul>
        ) : <p className="hint">{t("alliance.help.none")}</p>}
      </Panel>
      <Panel title={t("alliance.membership")}>
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          {!confirmLeave ? (
            <Button variant="ghost" onClick={() => setConfirmLeave(true)} data-testid="alliance-leave">{t("alliance.leave")}</Button>
          ) : (
            <>
              <span className="hint">{t("alliance.leaveConfirm")}</span>
              <Button variant="danger" onClick={() => void leave()} data-testid="alliance-leave-confirm">{t("alliance.leave")}</Button>
              <Button onClick={() => setConfirmLeave(false)}>{t("common.cancel")}</Button>
            </>
          )}
          {alliance.myRole === "leader" && alliance.memberCount === 1 ? (
            <Button variant="danger" onClick={() => void run(() => allianceApi.disband(), t("alliance.toast.disbanded")).then(() => refresh())} data-testid="alliance-disband">{t("alliance.disband")}</Button>
          ) : null}
        </div>
      </Panel>
    </div>
  );
}

/** Member rows with the actions the viewer's role allows: promote/demote, kick, transfer, message, visit. */
export function Members({ alliance }: { alliance: AllianceView }) {
  const t = useT();
  const me = useSession((s) => s.account);
  const run = useAlliance((s) => s.run);
  const refresh = useAlliance((s) => s.refresh);
  const openDm = useSocial((s) => s.openDm);
  const openProfile = useSocial((s) => s.openProfile);
  const myRole = alliance.myRole;
  const myRank = myRole ? allianceRank(myRole) : 99;
  const act = async (op: () => Promise<unknown>, ok?: string) => {
    if (await run(op, ok)) await refresh();
  };
  return (
    <div className="alliance-members" data-testid="alliance-members">
      {alliance.members.map((m: AllianceMember) => {
        const self = m.accountId === me?.id;
        const below = allianceRank(m.role) > myRank;
        const canPromote = !self && myRole !== null && allianceCan(myRole, "promote") && below;
        const canKick = !self && myRole !== null && allianceCan(myRole, "kick") && below;
        const canTransfer = !self && myRole === "leader";
        const grantable = ALLIANCE_ROLES.filter((r) => r !== "leader" && allianceRank(r) > myRank);
        return (
          <div key={m.accountId} className="alliance-row" data-testid={`alliance-member-${m.accountId}`} data-role={m.role}>
            <span className={`presence-dot ${m.online ? "on" : ""}`} />
            <span className="grow" style={{ minWidth: 0 }}>
              <button type="button" className="plain" style={{ fontWeight: 700 }} onClick={() => void openProfile(m.accountId)} data-testid="alliance-member-name">{m.name}</button>
              <span className="badge" style={{ marginLeft: 6 }}>{t(`alliance.role.${m.role}`)}</span>
              <span className="hint" style={{ display: "block" }}>HQ {m.hq} · {formatCompact(m.power)} · {m.online ? t("social.lastSeen.online") : lastSeenText({ kind: "minutes", n: Math.max(1, Math.round((serverNow() - m.lastSeenAt) / 60_000)) })}</span>
            </span>
            <div className="row alliance-actions" style={{ gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <Button style={{ minHeight: 32 }} onClick={() => navigate(`/app/visit/${m.accountId}`)} data-testid="alliance-member-visit"><Icon name="home" /> {t("alliance.visit")}</Button>
              {!self ? <Button style={{ minHeight: 32 }} onClick={() => void openDm(m.accountId, m.name).then(() => navigate("/app/chat"))} data-testid="alliance-member-message"><Icon name="mail" /></Button> : null}
              {canPromote ? (
                <select className="field" style={{ minHeight: 32 }} value={m.role} onChange={(e) => void act(() => allianceApi.setRole(m.accountId, e.target.value as AllianceRole), t("alliance.toast.role"))} data-testid="alliance-member-role">
                  {[m.role, ...grantable.filter((r) => r !== m.role)].map((r) => <option key={r} value={r}>{t(`alliance.role.${r}`)}</option>)}
                </select>
              ) : null}
              {canTransfer ? <Button style={{ minHeight: 32 }} onClick={() => void act(() => allianceApi.transfer(m.accountId), t("alliance.toast.transferred"))} data-testid="alliance-member-transfer"><Icon name="arrowUp" /> {t("alliance.transfer")}</Button> : null}
              {canKick ? <Button variant="danger" style={{ minHeight: 32 }} onClick={() => void act(() => allianceApi.kick(m.accountId), t("alliance.toast.kicked"))} data-testid="alliance-member-kick"><Icon name="close" /></Button> : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Requests({ alliance }: { alliance: AllianceView }) {
  const t = useT();
  const requests = useAlliance((s) => s.requests);
  const refreshRequests = useAlliance((s) => s.refreshRequests);
  const refresh = useAlliance((s) => s.refresh);
  const run = useAlliance((s) => s.run);
  useEffect(() => {
    void refreshRequests();
  }, [refreshRequests]);
  const officer = alliance.myRole !== null && allianceCan(alliance.myRole, "accept");
  const decide = async (id: string, accept: boolean) => {
    if (await run(() => (accept ? allianceApi.accept(id) : allianceApi.decline(id)), t(accept ? "alliance.toast.accepted" : "alliance.toast.declined"))) await refresh();
  };
  return (
    <div className="alliance-requests" data-testid="alliance-requests">
      {!officer ? <p className="hint">{t("alliance.requests.officersOnly")}</p> : null}
      {officer && requests && requests.applications.length === 0 ? <p className="hint" data-testid="alliance-requests-none">{t("alliance.requests.none")}</p> : null}
      {officer ? requests?.applications.map((r) => (
        <div key={r.id} className="alliance-row" data-testid={`alliance-request-${r.accountId}`}>
          <span className="grow" style={{ minWidth: 0 }}>
            <strong>{r.name}</strong> <span className="hint">HQ {r.hq} · {formatCompact(r.power)} · {relativeTime(r.createdAt, serverNow(), getLocale())}</span>
            {r.message ? <span className="hint" style={{ display: "block" }}>{r.message}</span> : null}
          </span>
          <Button variant="primary" style={{ minHeight: 34 }} onClick={() => void decide(r.id, true)} data-testid="alliance-request-accept">{t("alliance.accept")}</Button>
          <Button style={{ minHeight: 34 }} onClick={() => void decide(r.id, false)} data-testid="alliance-request-decline">{t("alliance.decline")}</Button>
        </div>
      )) : null}
    </div>
  );
}

function Log() {
  const t = useT();
  const log = useAlliance((s) => s.log);
  const refreshLog = useAlliance((s) => s.refreshLog);
  useEffect(() => {
    void refreshLog();
  }, [refreshLog]);
  if (!log) return <p className="hint" style={{ padding: 12 }}>{t("common.loading")}</p>;
  return (
    <div className="alliance-log" data-testid="alliance-log">
      {log.length === 0 ? <p className="hint">{t("alliance.log.none")}</p> : null}
      {log.map((l) => (
        <div key={l.id} className="row alliance-row" data-kind={l.kind}>
          <span className="grow">{t(`alliance.log.${l.kind}`, { name: l.actorName ?? "?", ...Object.fromEntries(Object.entries(l.payload).map(([k, v]) => [k, String(v)])) })}</span>
          <span className="hint mono">{relativeTime(l.createdAt, serverNow(), getLocale())}</span>
        </div>
      ))}
    </div>
  );
}
